import { useEffect, useRef } from 'react';

/**
 * Subscribes a callback to a Server-Sent Events stream while the
 * component is mounted. Auto-reconnects with exponential back-off if
 * the connection drops.
 *
 * The callback fires for every server-sent message; the caller
 * decides whether to refetch / merge / ignore based on the payload.
 *
 * The stream URL is hardcoded to /api/streams/... by callers — this
 * hook just owns the EventSource lifecycle and the reconnect logic.
 *
 * Returns nothing — the callback is the side-channel.
 */
export function useEventStream(
  url: string | null,
  onMessage: (data: unknown) => void,
  onError?: (e: Event) => void,
): void {
  // Store the callback in a ref so the EventSource is stable even if
  // the component re-renders with a new closure on every tick.
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!url) return;
    let closed = false;
    let attempt = 0;
    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const open = () => {
      if (closed) return;
      source = new EventSource(url);
      source.onmessage = (msg) => {
        attempt = 0; // reset back-off on any successful message
        try {
          onMessageRef.current(JSON.parse(msg.data));
        } catch {
          // non-JSON push (e.g. comment) — ignore
        }
      };
      source.onerror = (e) => {
        if (closed) return;
        if (onErrorRef.current) {
          try {
            onErrorRef.current(e);
          } catch {
            // swallow consumer errors
          }
        }
        // Browser auto-reconnects on transient errors, but if it
        // permanently fails we close + back-off + reopen ourselves.
        if (source && source.readyState === EventSource.CLOSED) {
          source.close();
          source = null;
          attempt += 1;
          const delayMs = Math.min(30_000, 500 * 2 ** Math.min(attempt, 6));
          reconnectTimer = setTimeout(open, delayMs);
        }
      };
    };

    open();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (source) source.close();
    };
  }, [url]);
}
